import { useCallback } from 'react';
import { extend } from 'lodash';
import { useFormSaveService, useFormSubmitWrapper } from '../../../../services/FormSaveService';

/**
 * Custom hook for handling infrastructure form submission
 * Supports both create and update operations
 *
 * @param {Object} config - Configuration object
 * @param {string} config.baseUrl - Base API URL for save operations
 * @param {string} config.goToUrl - URL to navigate after successful save
 * @param {Object} config.formData - Current form data
 * @param {Function} config.setError - React Hook Form setError function
 * @param {Function} config.saveErrorHandler - Error handler for save operations
 * @param {boolean} config.isCreateAction - Whether this is a create (vs update) action
 * @param {Function} config.createSubmitTransform - Optional transform function for create action data
 * @param {Function} config.submitTransform - Optional transform applied on both create and update submissions
 * @returns {Object} Submit handlers and state
 * @returns {boolean} saving - Whether a save operation is in progress
 * @returns {Function} handleSubmitFunction - Main submit function with navigation wrapper
 * @returns {Function} dirtyCheck - Function to check if form is dirty
 *
 * @example
 * function MyInfrastructureForm({ formData }) {
 *   const { saving, handleSubmitFunction, dirtyCheck } = useInfrastructureFormSubmit({
 *     baseUrl: '/api/infrastructure',
 *     goToUrl: '/infrastructure/list',
 *     formData,
 *     setError: methods.setError,
 *     saveErrorHandler: errorHandler,
 *     isCreateAction: !formData._id,
 *     createSubmitTransform: (data) => ({ ...data, type: 'server' })
 *   });
 *
 *   return <form onSubmit={handleSubmitFunction}>...</form>;
 * }
 */
export function useInfrastructureFormSubmit({
    baseUrl,
    goToUrl,
    formData,
    setError,
    saveErrorHandler,
    isCreateAction,
    createSubmitTransform,
    submitTransform,
}) {
    const formSaveService = useFormSaveService();
    const { submitWrapper, saving } = useFormSubmitWrapper();

    /**
     * Handle form submission for update action
     */
    const handleFormSubmit = useCallback(
        (submitData = {}) => {
            let dataToSave = {
                ...formData,
                ...submitData,
            };

            // Apply submitTransform (if provided) on both create and update paths.
            if (submitTransform) {
                dataToSave = submitTransform(dataToSave);
            }

            return formSaveService.save({
                url: baseUrl,
                recordId: formData._id,
                dataToSave: extend(dataToSave, { _id: formData._id }),
                setError,
                saveErrorHandler,
            });
        },

        [formData, formSaveService, baseUrl, setError, saveErrorHandler, submitTransform]
    );

    /**
     * Handle form submission for create action
     */
    const handleCreateSubmit = useCallback(
        (dataToSave = {}) => {
            // Apply submitTransform first (general transform for both paths),
            // then createSubmitTransform (create-only transform, e.g., sets insertTime).
            const afterSubmitTransform = submitTransform ? submitTransform(dataToSave) : dataToSave;
            const transformedData = createSubmitTransform
                ? createSubmitTransform(afterSubmitTransform)
                : afterSubmitTransform;

            return formSaveService.save({
                url: baseUrl,
                dataToSave: transformedData,
                setError,
                saveErrorHandler,
            });
        },
        [formSaveService, saveErrorHandler, setError, baseUrl, createSubmitTransform, submitTransform]
    );

    /**
     * Main submit function wrapped with navigation
     */
    const handleSubmitFunction = submitWrapper(isCreateAction ? handleCreateSubmit : handleFormSubmit, goToUrl);

    /**
     * Simple dirty check function
     */
    const dirtyCheck = useCallback((isDirty) => isDirty, []);

    return {
        saving,
        handleSubmitFunction,
        dirtyCheck,
    };
}
